import { formatCurrency, formatPercent } from '@/utils/money';
import { getTranslationValue } from '@/utils/translations';
import { HubCardType, WeeklyProfitRollupHubCardData } from '@/types';
import {
  InsightGrowthDirection,
  InsightGrowthStat,
  ProfitRollupHubCardData,
} from '@/experiences/sales-portal/domain/insights/blocs/types/insight';
import { toFixedDecimalPercentage } from '@/utils/percentage';

const toGrowthDirection = (delta: number): InsightGrowthDirection => {
  if (delta > 0) {
    return InsightGrowthDirection.Up;
  }
  return delta < 0 ? InsightGrowthDirection.Down : InsightGrowthDirection.Flat;
};

const toGrowthStat = (
  formattedValue: string,
  delta: number | null | undefined,
): InsightGrowthStat => ({
  delta:
    delta === null || delta === undefined
      ? undefined
      : {
          direction: toGrowthDirection(delta),
          formattedValue: formatPercent(Math.abs(delta)),
        },
  formattedValue,
});

/**
 * Transforms insight hub card data returned by `getHubCardData` into the
 * domain {@link ProfitRollupHubCardData} presentation data.
 *
 * @param {string[]}  cardDataJson - Stringified `WeeklyProfitRollupHubCardData` entries
 *   from `getHubCardData`.
 * @param {boolean} isGrossProfitDisabled - boolean flag returned by backend used to hide gross profit data
 *
 * @returns The {@link ProfitRollupHubCardData} data, or `null` when the
 *   payload is empty or not a profit rollup card (which will hide the card).
 */
export const transformProfitRollup = (
  cardDataJson: string[],
  isGrossProfitDisabled: boolean,
): ProfitRollupHubCardData | null => {
  const [json] = cardDataJson;
  if (!json) {
    return null;
  }

  const raw = JSON.parse(json) as WeeklyProfitRollupHubCardData;

  // hide card if card_type mismatch
  if (raw.card_type !== HubCardType.WeeklyProfitRollup) {
    return null;
  }

  return {
    avgGpPercentage:
      isGrossProfitDisabled ||
      raw.avg_gp_percentage === null ||
      raw.avg_gp_percentage === undefined
        ? undefined
        : toGrowthStat(
            toFixedDecimalPercentage(raw.avg_gp_percentage),
            raw.avg_gp_percentage_delta,
          ),
    subtitle: getTranslationValue(raw.subtitle),
    title: getTranslationValue(raw.title),
    totalSales: toGrowthStat(
      formatCurrency(raw.total_sales_in_micros, raw.currency_code),
      raw.total_sales_percentage_delta,
    ),
  };
};
